import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from 'react-query'
import { toast } from 'react-toastify'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, CircularProgress, Typography } from '@mui/material'
import { AuthLogo, Button, Link, LoginInput } from 'components'
import { isObjectEmpty } from 'utils'
import { successToast } from 'utils/toastService'
import { ErrorContainer, StyledForm } from './ForgotPasswordPage.styled'
import EmailIcon from '../../../assets/svgComponents/EmailIcon'
import useUserService from '../../../services/userService'

const ForgotPasswordValidationSchema = yup.object().shape({
  email: yup
    .string()
    .email('Invalid email format')
    .required('Email is required.'),
})

export const ForgotPasswordPage = () => {
  const navigate = useNavigate()
  const { forgotPassword } = useUserService()

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(ForgotPasswordValidationSchema),
  })

  const { mutate: sendResetEmail, isLoading } = useMutation(
    (email: string) => forgotPassword(email),
    {
      onSuccess: () => {
        successToast('If this email exists, reset instructions have been sent.')
        navigate('/auth/login')
      },
      onError: (error: any) => {
        const msg = error?.detail || 'Failed to request reset email'
        toast.error(msg)
      },
    }
  )

  const onSubmit = (data: { email: string }) => {
    sendResetEmail(data.email)
  }

  const isFormValid = useMemo(() => isObjectEmpty(errors), [errors])

  return (
    <Box
      width="360px"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      gap="40px"
    >
      <AuthLogo />
      <StyledForm onSubmit={handleSubmit(onSubmit)}>
        <Box display="flex" flexDirection="column">
          <Controller
            name="email"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <LoginInput
                placeholder="Email"
                type="email"
                error={!!errors['email']}
                cut="none"
                preIcon={<EmailIcon />}
                {...field}
              />
            )}
          />
        </Box>

        <ErrorContainer>
          {!isFormValid && <Typography>{errors['email']?.message}</Typography>}
        </ErrorContainer>

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Link to="/auth/login">Login</Link>
          <Button
            disabled={isLoading}
            type="submit"
            startIcon={
              isLoading && <CircularProgress color="inherit" size={24} />
            }
          >
            Request Reset Link
          </Button>
        </Box>
      </StyledForm>
    </Box>
  )
}
